import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type { RouteLocationNormalized } from 'vue-router'

/**
 * 页签导航 —— 取代旧工程「每个功能一个独立 HTML 页面」的 MPA 导航方式。
 *
 * 旧行为回顾（index.html + leftmenu.js）：
 *   从门户宫格点击 -> window.location.href = menuUrl?menuId=xx&menuSubId=yy
 *   即整页跳转，无法保留多任务上下文，也没有页签。
 *
 * 新行为：SPA 内以页签承载，可保留多个功能同时打开，
 *   并且保留 menuId / menuSubId 参数，使后端权限校验逻辑无需改动。
 */

export interface TabItem {
  /** 唯一 key，取路由 fullPath */
  key: string
  title: string
  name?: string
  path: string
  query?: Record<string, any>
  affix?: boolean
  /** 旧工程的 menuId */
  menuId?: string
  /** 旧工程的 menuSubId */
  menuSubId?: string
}

const AFFIX_TABS: TabItem[] = [
  { key: '/index', title: '首页', path: '/index', affix: true }
]

export const useTabsStore = defineStore('tabs', () => {
  const tabs = ref<TabItem[]>([...AFFIX_TABS])
  const active = ref<string>('/index')

  const activeTab = computed(() => tabs.value.find((t) => t.key === active.value))

  function add(route: RouteLocationNormalized) {
    if (!route.name || route.meta?.hideTab) return
    const key = route.fullPath
    const exist = tabs.value.find((t) => t.key === key)
    if (!exist) {
      tabs.value.push({
        key,
        title: (route.meta?.title as string) || (route.name as string),
        name: route.name as string,
        path: route.path,
        query: route.query,
        menuId: route.query.menuId as string,
        menuSubId: route.query.menuSubId as string
      })
    }
    active.value = key
  }

  function close(key: string) {
    const idx = tabs.value.findIndex((t) => t.key === key)
    if (idx === -1) return
    const tab = tabs.value[idx]
    if (tab.affix) return
    tabs.value.splice(idx, 1)
    if (active.value === key) {
      const next = tabs.value[idx - 1] || tabs.value[idx] || tabs.value[0]
      active.value = next ? next.key : '/index'
    }
  }

  function closeOthers(key: string) {
    tabs.value = tabs.value.filter((t) => t.affix || t.key === key)
    active.value = key
  }

  function closeAll() {
    tabs.value = tabs.value.filter((t) => t.affix)
    active.value = tabs.value[0]?.key || '/index'
  }

  function reset() {
    tabs.value = [...AFFIX_TABS]
    active.value = '/index'
  }

  return { tabs, active, activeTab, add, close, closeOthers, closeAll, reset }
})
